if (!customElements.get('collection-load-more')) {
  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    connectedCallback() {
      this.init();
      this.handleInitialPageParam();
    }

    handleInitialPageParam() {
      if (window.location.search && window.location.search.includes('page=')) {
        try {
          const url = new URL(window.location.href);
          const pageParam = url.searchParams.get('page');
          const pageNum = parseInt(pageParam, 10);

          url.searchParams.delete('page');
          const cleanUrl = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
          window.history.replaceState({}, '', cleanUrl);

          // If user landed on page > 1, replace location cleanly to page 1 so all initial 30 products and Load More button load properly
          if (pageNum > 1) {
            window.location.replace(cleanUrl);
          }
        } catch (e) {
          console.warn('Could not clean page param:', e);
        }
      }
    }

    init() {
      this.button = this.querySelector('.load-more-btn');
      this.paginationType = this.dataset.paginationType || 'load_more';
      this.isLoading = false;

      if (this.button && !this.button._hasLoadMoreListener) {
        this.button._hasLoadMoreListener = true;
        this.button.addEventListener('click', this.onButtonClick.bind(this));
      }

      if (this.paginationType === 'infinite_scroll') {
        this.initInfiniteScroll();
      }
    }

    initInfiniteScroll() {
      if (!('IntersectionObserver' in window)) return;
      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isLoading) {
              const nextUrl = this.button?.dataset?.nextUrl;
              if (nextUrl) {
                this.loadMore(nextUrl);
              }
            }
          });
        },
        { rootMargin: '300px 0px' }
      );

      if (this.button) {
        this.observer.observe(this.button);
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      if (this.isLoading) return;
      const nextUrl = this.button?.dataset?.nextUrl;
      if (nextUrl) {
        this.loadMore(nextUrl);
      }
    }

    async loadMore(nextUrl) {
      if (!nextUrl || this.isLoading) return;
      this.isLoading = true;
      if (this.button) {
        this.button.classList.add('is-loading');
        this.button.setAttribute('disabled', 'disabled');
      }

      try {
        const response = await fetch(nextUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // 1. Append new grid items
        const newItems = html.querySelectorAll('#product-grid > .grid__item');
        const currentGrid = document.getElementById('product-grid');

        if (currentGrid && newItems.length > 0) {
          newItems.forEach((item) => {
            currentGrid.appendChild(item);
          });
        }

        // 2. Update Load More component & progress
        const newLoadMore = html.querySelector('collection-load-more');
        const newBtn = newLoadMore?.querySelector('.load-more-btn');
        const newProgress = newLoadMore?.querySelector('.load-more-progress');

        if (newProgress) {
          const currentProgress = this.querySelector('.load-more-progress');
          if (currentProgress) {
            currentProgress.innerHTML = newProgress.innerHTML;
          }
        }

        if (newBtn && newBtn.dataset.nextUrl) {
          if (this.button) {
            this.button.dataset.nextUrl = newBtn.dataset.nextUrl;
            this.button.classList.remove('is-loading');
            this.button.removeAttribute('disabled');
            if (this.paginationType === 'infinite_scroll' && this.observer) {
              this.observer.observe(this.button);
            }
          }
        } else {
          // No more pages left
          const btnWrap = this.querySelector('.load-more-btn-wrap');
          if (btnWrap) {
            btnWrap.innerHTML = '<p class="load-more-end-text">You have viewed all products</p>';
          } else if (this.button) {
            this.button.remove();
          }
          if (this.observer) {
            this.observer.disconnect();
          }
        }

        // 3. Keep URL clean without ?page= parameter so page reloads always load from page 1 with Load More button
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('page');
          const cleanUrl = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
          window.history.replaceState({}, '', cleanUrl);
        } catch (e) {
          // Ignore URL replace errors
        }

        // 4. Trigger custom event for theme and app compatibility
        document.dispatchEvent(
          new CustomEvent('collection:loaded-more', {
            detail: { url: nextUrl, count: newItems.length }
          })
        );
      } catch (error) {
        console.error('Failed to load more products:', error);
        if (this.button) {
          this.button.classList.remove('is-loading');
          this.button.removeAttribute('disabled');
        }
      } finally {
        this.isLoading = false;
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);
}
